import { Link } from '@/components/primitives/Link';
import { TransText } from '@/i18n/trans/text';
import type { ProfileBookingType } from '@/types/profile';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { Button, Icons, Typography } from '@fleet/webshop-ui';
import { type FC, type PropsWithChildren } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';

const layoutConfig = {
  tickets: {
    icon: Icons.ticket,
    header: 'tickets',
    prefixUrl: '/account/tickets',
    menuItems: [
      ['upcoming', 'upcoming', <Icons.transportFrom width={24} height={24} />],
      ['history', 'history', <Icons.history width={24} height={24} />],
    ],
  },
  travelPasses: {
    icon: Icons.travelPass,
    header: 'travelPasses',
    prefixUrl: '/account/travel-passes',
    menuItems: [
      ['active', 'active', <Icons.transportFrom width={24} height={24} />],
      ['history', 'history', <Icons.history width={24} height={24} />],
    ],
  },
  vouchers: {
    icon: Icons.email,
    header: 'vouchers',
    prefixUrl: '/account/vouchers',
    menuItems: [
      ['active', 'active', <Icons.transportFrom width={24} height={24} />],
      ['history', 'history', <Icons.history width={24} height={24} />],
    ],
  },
  accountDetails: {
    icon: Icons.user,
    header: 'accountDetails',
    prefixUrl: '/account/details',
    menuItems: [
      ['myInformation', 'info', <Icons.user width={24} height={24} />],
      ['accountSettings', 'settings', <Icons.unlock width={24} height={24} />],
      ['loyalty', 'loyalty', <Icons.loyalty width={24} height={24} />],
    ],
  },
} as const;

interface HistoryLayoutProps extends PropsWithChildren {
  bookingType: ProfileBookingType;
}

const HistoryLayout: FC<HistoryLayoutProps> = ({
  bookingType,
  children,
}: HistoryLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const {
    icon: Icon,
    header,
    prefixUrl,
    menuItems,
  } = layoutConfig[bookingType];

  return (
    <div className="mx-auto flex w-full max-w-laptop flex-col gap-1 laptop:gap-6 laptop:px-4">
      <div className="flex items-center justify-between border-b border-neutral px-4 py-2 laptop:px-0 laptop:py-4">
        <div className="flex items-center gap-3">
          <Icon className="h-6 w-6 text-accent" />
          <Typography
            variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
            as="h1"
          >
            <TransText i18nKey={header} />
          </Typography>
        </div>
        {bookingType !== 'accountDetails' && (
          <Button
            data-testid="profile-new-purchase"
            onClick={() => {
              const productType =
                bookingType === 'tickets' ? 'ticket' : 'travelPass';
              navigate(`/?productType=${productType}`);
            }}
            startAdornment={<Icons.addCircle className="h-5 w-5" />}
            className="w-fit"
          >
            <TransText i18nKey="new" />
          </Button>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-4 px-4 pb-6 pt-2 laptop:flex-row laptop:gap-16 laptop:p-0 laptop:pb-16">
        <div className="flex gap-6 border-b border-neutral laptop:mr-5 laptop:min-w-[160px] laptop:flex-col laptop:items-start laptop:gap-0 laptop:border-b-0 laptop:border-r">
          {menuItems.map(([transKey, url, Icon], key) => {
            const to = `${prefixUrl}/${url}`;
            const isActive = location.pathname === to;

            return (
              <Link
                key={key}
                to={to}
                className={cn(
                  'relative flex flex-col justify-between gap-1 pb-3 pt-2 laptop:w-full laptop:flex-row laptop:gap-3 laptop:py-5',
                  { 'text-accent laptop:border-r': isActive }
                )}
              >
                <div className="flex items-center gap-2 laptop:flex-row laptop:items-start">
                  {Icon}
                  <Typography
                    variant={isLaptopOrBigger ? 'body1-bold' : 'body2-bold'}
                    className="mr-5"
                  >
                    <TransText i18nKey={transKey} />
                  </Typography>
                </div>
                {isActive && (
                  <div className="absolute -bottom-[1px] left-0 right-0 h-[3px] rounded-t-full bg-accent laptop:-right-[2px] laptop:left-auto laptop:top-0 laptop:h-auto laptop:w-[3px] laptop:rounded-none laptop:rounded-l-full" />
                )}
              </Link>
            );
          })}
        </div>
        {children}
      </div>
    </div>
  );
};

export default HistoryLayout;
